// 지투파워 전용 단일 UI
// - 입력: SelJb(선택된 지투파워 장비의 seq)
// - 동작: SelJb와 같은 통신 포트/주소를 우선 기준으로 엠알티-표준 접속함을 페어로 찾아 함께 표시
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../App.css';
import { CSVLink } from 'react-csv';
import { ko } from 'date-fns/locale';
import config from '../../config';
import { findSeqIndex } from '../../utils/seqDataUtils';

function JbG2PowerSet({ SelJb }) {
  const Jb_list = useSelector((state) => state.Jb_list || {});
  const JbMrtData = useSelector((state) => state.JbMrtData || {});
  // console.log('[G2 UI] JbMrtData:', JbMrtData);
  const [index, setIndex] = useState(null);

  useEffect(() => {
    // SelJb(seq)에 해당하는 현재 선택 장비의 인덱스를 계산
    if (Jb_list.seq && Array.isArray(Jb_list.seq)) {
      const foundIndex = Jb_list.seq.findIndex((seq) => SelJb === seq);
      setIndex(foundIndex !== -1 ? foundIndex : null);
      if (foundIndex !== -1) {
        try {
          // console.log('[G2 UI] selected seq:', SelJb, 'index:', foundIndex, 'pair_device:', Jb_list.pair_device?.[foundIndex], 'pair_seq(legacy):', Jb_list.pair_seq?.[foundIndex]);
        } catch (e) {}
      }
    }
  }, [Jb_list, SelJb]);

  const pairSeq = useMemo(() => {
    // 0) 등록 시 저장된 pair_device(최신) 또는 pair_seq(레거시)가 있으면 그대로 사용
    if (index === null || index === undefined) return null;
    const presetPair = Jb_list.pair_device?.[index] ?? Jb_list.pair_seq?.[index];
    if (presetPair) {
      try { /* console.log('[G2 UI] use preset pair:', presetPair); */ } catch(e) {}
      return presetPair;
    }

    // 1) 같은 포트 + (jb_address + 1) + '엠알티-표준'
    // 2) 같은 포트 + 같은 주소 + '엠알티-표준'
    // 3) 같은 포트의 첫 번째 '엠알티-표준'
    const selectedComm = Jb_list.jb_commPort?.[index];
    const selectedAddr = Number(Jb_list.jb_address?.[index]);
    const addrPlusOne = Number.isFinite(selectedAddr) ? selectedAddr + 1 : undefined;

    let pairIdx = (Jb_list.seq || []).findIndex((_, i) =>
      Jb_list.jb_protocol?.[i] === '엠알티-표준' &&
      Jb_list.jb_commPort?.[i] === selectedComm &&
      Number(Jb_list.jb_address?.[i]) === addrPlusOne
    );

    if (pairIdx === -1) {
      pairIdx = (Jb_list.seq || []).findIndex((_, i) =>
        Jb_list.jb_protocol?.[i] === '엠알티-표준' &&
        Jb_list.jb_commPort?.[i] === selectedComm &&
        Number(Jb_list.jb_address?.[i]) === selectedAddr
      );
    }

    if (pairIdx === -1) {
      pairIdx = (Jb_list.seq || []).findIndex((_, i) =>
        Jb_list.jb_protocol?.[i] === '엠알티-표준' &&
        Jb_list.jb_commPort?.[i] === selectedComm
      );
    }

    const rv = pairIdx !== -1 ? Jb_list.seq?.[pairIdx] : null;
    try { /* console.log('[G2 UI] resolved pair by heuristic:', rv); */ } catch(e) {}
    return rv;
  }, [Jb_list, index]);

  const pairIndex = useMemo(() => {
    if (!pairSeq || !Array.isArray(Jb_list.seq)) return null;
    const pIdx = Jb_list.seq.findIndex((s) => s === pairSeq);
    try { /* console.log('[G2 UI] pairIndex:', pIdx); */ } catch(e) {}
    return pIdx !== -1 ? pIdx : null;
  }, [Jb_list, pairSeq]);
  // 렌더링용 인덱스 구분: 채널은 G2, 요약(통합/TEV)은 MRT(없으면 G2)
  const g2Index = index;
  const g2DataIndex = useMemo(() => findSeqIndex(JbMrtData, SelJb), [JbMrtData, SelJb]);
  const summarySeq = useMemo(() => pairSeq ?? SelJb, [pairSeq, SelJb]);
  const summaryDataIndex = useMemo(() => findSeqIndex(JbMrtData, summarySeq), [JbMrtData, summarySeq]);
  const channelSeq = summarySeq;
  const channelDataIndex = summaryDataIndex;

  // 활성 채널(지투파워 장비 기준)
  const activeChannels = useMemo(() => {
    if (index === null || index === undefined) return [];
    const channelUseArray = Jb_list.jb_pv_ch_use && Jb_list.jb_pv_ch_use[index]
      ? Jb_list.jb_pv_ch_use[index].split(',')
      : [];
    const channelNameArray = Jb_list.jb_pv_ch_name && Jb_list.jb_pv_ch_name[index]
      ? Jb_list.jb_pv_ch_name[index].split(',')
      : [];
    return channelUseArray
      .map((use, i) => (use === '1' ? { name: channelNameArray[i], index: i } : null))
      .filter((ch) => ch !== null);
  }, [Jb_list, index]);

  const cellStyle = {
    minWidth: '150px',
    width: '150px',
    padding: '8px',
    textAlign: 'center',
    verticalAlign: 'middle',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  };

  // 지투파워 전용 파생 값
  const relayOnCount = useMemo(() => {
    if (g2DataIndex === -1) return 0;
    const arr = JbMrtData.relayStatus?.[g2DataIndex] || [];
    return Array.isArray(arr) ? arr.filter((v) => v === 1).length : 0;
  }, [JbMrtData.relayStatus, g2DataIndex]);

  const arcTotal = useMemo(() => {
    if (g2DataIndex === -1) return 0;
    const arr = JbMrtData.arcCount?.[g2DataIndex] || [];
    return Array.isArray(arr) ? arr.reduce((sum, v) => sum + (Number(v) || 0), 0) : 0;
  }, [JbMrtData.arcCount, g2DataIndex]);

  // 경보해제 패킷 생성/전송 유틸 (Modbus FC06, 주소 0x04B0)
  // 경보 해제 기능 제거됨

  return (
    <div style={{ width: '100%' }}>
      <div style={{ margin: '10px', maxWidth: '1560px' }}>
        <div className="JbGenTable" style={{ paddingTop: '20px' }}>
          {/* 첫 번째 요약 표 제거됨 */}

          {/* 지투파워 비교 테이블: 두 개의 테이블로 분할(최대 3행씩) */}
          <div className="summary-table-container" style={{ marginTop: pairIndex !== null ? '-10px' : '0px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <table className="summary-table" style={{ marginBottom: '10px', width: '50%' }}>
                <thead>
                  <tr>
                    <th>항목</th>
                    <th>값</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>통합전압 (V) {/* MRT 요약 */}</td>
                    <td>{((JbMrtData.PvTotalVolt?.[summaryDataIndex] || 0))}</td>
                  </tr>
                  <tr>
                    <td>통합전류 (A) {/* MRT 요약 */}</td>
                    <td>{((JbMrtData.pvTotalCurrent?.[summaryDataIndex] || 0)).toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>통합전력 (kW)</td>
                    <td>{(() => {
                      const v = Number(JbMrtData.PvTotalVolt?.[summaryDataIndex] || 0);
                      const c = Number(JbMrtData.pvTotalCurrent?.[summaryDataIndex] || 0);
                      const kw = (v * c) / 1000;
                      return `${Number.isFinite(kw) ? kw.toFixed(2) : '0.00'} kW`;
                    })()}</td>
                  </tr>
                </tbody>
              </table>
              <table className="summary-table" style={{ marginBottom: '10px', width: '50%' }}>
                <thead>
                  <tr>
                    <th>항목</th>
                    <th>값</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>TEV dB / CNT </td>
                    <td>{(JbMrtData.tevDb?.[g2DataIndex] ?? 0)} / {(JbMrtData.tevCnt?.[g2DataIndex] ?? 0)}</td>
                  </tr>
                  <tr>
                    <td>아크 설정값</td>
                    <td>{JbMrtData.setarcCount?.[g2DataIndex] ?? 0}</td>
                  </tr>
                  <tr>
                    <td>온도 설정값</td>
                    <td>{JbMrtData.setTemp?.[g2DataIndex] ?? 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 지투파워 채널 표 (선택된 장비 기준) */}
          {g2Index !== null && (
            <div className="Bni-table-container" style={{ marginTop: '-15px' }}>
              <table className="Bni-channel-info-table">
                <thead>
                  <tr>
                    <th style={cellStyle}>{Jb_list.jb_name?.[g2Index]}</th>
                    {activeChannels.map((channel) => (
                      <th key={channel.index}>{channel.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>전압 (V) {/* 겹침 */}</td>
                    {activeChannels.map((channel, i) => (
                      <td key={`volt-${channel.index}`}>{((JbMrtData.pvChVolt?.[channelDataIndex]?.[i] || 0))}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>전류 (A) {/* 겹침 */}</td>
                    {activeChannels.map((channel, i) => (
                      <td key={`current-${channel.index}`}>{((JbMrtData.pvChCurrent?.[channelDataIndex]?.[i] || 0) / 10).toFixed(1)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>전력 (kW)</td>
                    {activeChannels.map((channel, i) => {
                      const v = Number(JbMrtData.pvChVolt?.[channelDataIndex]?.[i] || 0);
                      const c = Number(JbMrtData.pvChCurrent?.[channelDataIndex]?.[i] || 0);
                      const kw = ((v * c) / 100000);
                      return (
                        <td key={`power-${channel.index}`}>{Number.isFinite(kw) ? kw.toFixed(2) : '0.00'}</td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td>아크 개수</td>
                    {activeChannels.map((channel, i) => (
                      <td key={`arc-${channel.index}`}>
                        {JbMrtData.arcCount?.[g2DataIndex]?.[i] ?? 0}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>dc-arc</td>
                    {activeChannels.map((channel, i) => (
                      <td key={`dc-arc-${channel.index}`}>
                        {JbMrtData.relayStatus?.[g2DataIndex]?.[i] === 1 ? 'ON' : 'OFF'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 시간대별 그래프: 스트링 전압/전류는 표준-엠알티 기준 사용 */}
      {channelSeq != null && (
        <ChannelCurrentGraph activeChannels={activeChannels} seq={channelSeq} height={'320px'} />
      )}
    </div>
  );
}

export default JbG2PowerSet;

// === 시간대별 그래프 컴포넌트 (지투파워 seq 기반) ===
function ChannelCurrentGraph({ activeChannels, seq, height }) {
  const timeLabels = useMemo(
    () => [
      '05:00','05:10','05:20','05:30','05:40','05:50','06:00','06:10','06:20','06:30','06:40','06:50',
      '07:00','07:10','07:20','07:30','07:40','07:50','08:00','08:10','08:20','08:30','08:40','08:50',
      '09:00','09:10','09:20','09:30','09:40','09:50','10:00','10:10','10:20','10:30','10:40','10:50',
      '11:00','11:10','11:20','11:30','11:40','11:50','12:00','12:10','12:20','12:30','12:40','12:50',
      '13:00','13:10','13:20','13:30','13:40','13:50','14:00','14:10','14:20','14:30','14:40','14:50',
      '15:00','15:10','15:20','15:30','15:40','15:50','16:00','16:10','16:20','16:30','16:40','16:50',
      '17:00','17:10','17:20','17:30','17:40','17:50','18:00','18:10','18:20','18:30','18:40','18:50',
      '19:00','19:10','19:20','19:30','19:40','19:50','20:00'
    ],
    []
  );

  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hiddenState, setHiddenState] = useState({});
  const [chartDataByTime, setChartDataByTime] = useState({});
  const chartRef = useRef(null);
  const [PvTotalVolt, setPvTotalVolt] = useState(Array(timeLabels.length).fill(0));
  const [PvTotalCurrent, setPvTotalCurrent] = useState(Array(timeLabels.length).fill(0));
  const [PvTemperature, setPvTemperature] = useState(Array(timeLabels.length).fill(0));
  

  const selectedDateStr = selectedDate.toISOString().split('T')[0];

  // 안전 포맷 유틸
  const formatDeci = useCallback((value, digits = 1) => {
    const num = typeof value === 'number' ? value : Number(value);
    if (!isFinite(num)) return 'N/A';
    return (num / 10).toFixed(digits);
  }, []);
  const toSlotLabelUTC = useCallback((createdAt) => {
    const d = new Date(createdAt);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(Math.floor(d.getUTCMinutes() / 10) * 10).padStart(2, '0');
    return `${hh}:${mm}`;
  }, []);

  const setEmptyData = useCallback(() => {
    const emptyChartData = {};
    activeChannels.forEach((channel) => { emptyChartData[channel.index] = Array(timeLabels.length).fill(null); });
    setChartDataByTime(emptyChartData);
    setPvTotalVolt(Array(timeLabels.length).fill('N/A'));
    setPvTotalCurrent(Array(timeLabels.length).fill('N/A'));
    setPvTemperature(Array(timeLabels.length).fill('N/A'));
    setIsDataAvailable(false);
  }, [activeChannels, timeLabels]);

  const fetchData = useCallback(
    async (date) => {
      setIsLoading(true);
      try {
        const selectedDateInKST = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
          .toISOString()
          .split('T')[0];
        const response = await fetch(`${config.backendUrl}/get_hourly_jb_mrt_data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: selectedDateInKST }),
        });
        const result = await response.json();

        if (!result.data || result.data.length === 0) {
          setEmptyData();
          return;
        }

        const filtered = Array.isArray(result.data) ? result.data : [];
        const filteredBySeq = seq ? filtered.filter((it) => it.seq === seq) : [];

        // 시간 라벨 → 데이터 매핑 (UTC 10분 단위)
        const timeLabelSet = new Set(timeLabels);
        const slotToItem = {};
        for (const item of filteredBySeq) {
          const slot = toSlotLabelUTC(item.created_at);
          if (timeLabelSet.has(slot)) slotToItem[slot] = item;
        }

        // 결과 버퍼 준비
        const chartData = {};
        activeChannels.forEach((channel) => { chartData[channel.index] = Array(timeLabels.length).fill(null); });
        const newPvTotalVolt = Array(timeLabels.length).fill('N/A');
        const newPvTotalCurrent = Array(timeLabels.length).fill('N/A');
        const newPvTemperature = Array(timeLabels.length).fill('N/A');

        timeLabels.forEach((label, timeIndex) => {
          const item = slotToItem[label];
          if (!item) return;
          const pvChCurrentArray = item.PvChCurrent ? item.PvChCurrent.split(',').map(Number) : Array(32).fill(null);
          newPvTotalVolt[timeIndex] = item.PvTotalVolt ?? 'N/A';
          newPvTotalCurrent[timeIndex] = item.PvTotalCurrent ?? 'N/A';
          newPvTemperature[timeIndex] = item.temperature ?? 'N/A';
          activeChannels.forEach((channel) => {
            chartData[channel.index][timeIndex] = pvChCurrentArray[channel.index] ?? null;
          });
        });

        setChartDataByTime(chartData);
        setPvTotalVolt(newPvTotalVolt);
        setPvTotalCurrent(newPvTotalCurrent);
        setPvTemperature(newPvTemperature);
        setIsDataAvailable(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setEmptyData();
      } finally {
        setIsLoading(false);
      }
    },
    [activeChannels, seq, timeLabels, toSlotLabelUTC, setEmptyData]
  );

  useEffect(() => { if (seq) { fetchData(selectedDate); } }, [fetchData, selectedDate, seq]);
  useEffect(() => { const id = setInterval(() => fetchData(new Date()), 660000); return () => clearInterval(id); }, [fetchData]);

  const generateCSVData = () => {
    const csvData = [];
    timeLabels.forEach((label, index) => {
      const row = {
        time: label,
        PvTotalVolt: formatDeci(PvTotalVolt[index], 1),
        PvTotalCurrent: formatDeci(PvTotalCurrent[index], 1),
        Temperature: formatDeci(PvTemperature[index], 1),
      };
      activeChannels.forEach((channel) => {
        const val = chartDataByTime[channel.index]?.[index];
        row[`Channel ${channel.index + 1} Current`] =
          val === null || val === undefined || val === 0
            ? null
            : formatDeci(val, 1);
      });
      csvData.push(row);
    });
    return csvData;
  };

  const csvHeaders = [
    { label: 'Time', key: 'time' },
    { label: 'PvTotalVolt', key: 'PvTotalVolt' },
    { label: 'PvTotalCurrent', key: 'PvTotalCurrent' },
    { label: 'Temperature', key: 'Temperature' },
    ...activeChannels.map((channel) => ({ label: `Channel ${channel.index + 1} Current`, key: `Channel ${channel.index + 1} Current` })),
  ];

  const chartData = {
    labels: timeLabels,
    datasets: activeChannels.map((channel, index) => ({
      label: channel.name,
      data: chartDataByTime[channel.index]?.map((value) => (value === 0 ? null : value)) || [],
      borderColor: [
        '#EF5350','#26C6DA','#FF7043','#AB47BC','#FFCA28','#3A736A','#7E57C2','#60A07A','#FFA726','#8D6E63',
        '#66BB6A','#D44A46','#7F8C8D','#4B4B3D','#918D62','#DAB672','#F1D597','#F4B381','#F19557','#E05D44',
        '#C25054','#AC6D82','#26A69A','#6B4E3D'
      ][index % 24],
      borderWidth: 1,
      fill: false,
      tension: 0.4,
      hidden: hiddenState[channel.index] || false,
      pointRadius: chartDataByTime[channel.index]?.map((value) => (value === null ? 0 : 1)),
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: true,
        title: { display: true, text: '전류 (A)' },
        min: 0,
        ticks: { callback: function (value) { return (value / 10).toFixed(1); } },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: { boxWidth: 15, padding: 15, font: { size: 12 }, usePointStyle: true },
        onClick: (e, legendItem) => {
          const idx = legendItem.datasetIndex;
          const channelIndex = activeChannels[idx].index;
          setHiddenState((prev) => ({ ...prev, [channelIndex]: !prev[channelIndex] }));
        },
      },
      tooltip: {
        mode: 'index', intersect: false, position: 'nearest', yAlign: 'center', xAlign: 'right', caretPadding: 30, caretSize: 10,
        callbacks: {
          title: function (tooltipItems) {
            const dataIndex = tooltipItems[0].dataIndex;
            const timeLabel = timeLabels[dataIndex];
            const Pvvolt = PvTotalVolt[dataIndex];
            const Pvcurrent = PvTotalCurrent[dataIndex];
            const temperature = PvTemperature[dataIndex];
            return [
              `시간: ${timeLabel}`,
              `통합전압: ${formatDeci(Pvvolt)}V`,
              `통합전류: ${formatDeci(Pvcurrent)}A`,
              `온도: ${formatDeci(temperature)}℃`,
            ];
          },
          label: function (tooltipItem) {
            const channelIndex = activeChannels[tooltipItem.datasetIndex]?.index;
            if (channelIndex === undefined || !chartDataByTime[channelIndex]) return '';
            const value = chartDataByTime[channelIndex][tooltipItem.dataIndex] || 0;
            return [`${tooltipItem.dataset.label}: ${(value / 10).toFixed(1)}A`];
          },
        },
        bodyFont: { size: 10 },
        titleFont: { size: 12 },
      },
    },
  };

  const handleSelectAll = () => { const all = {}; activeChannels.forEach((ch) => { all[ch.index] = false; }); setHiddenState(all); };
  const handleDeselectAll = () => { const all = {}; activeChannels.forEach((ch) => { all[ch.index] = true; }); setHiddenState(all); };

  return (
    <div style={{ margin: '10px', maxWidth: '1560px', backgroundColor: 'white', paddingTop: '5px', borderRadius: '10px', boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)' }}>
      <div className="control-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', marginBottom: '10px' }}>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="yyyy-MM-dd"
          locale={ko}
          className="styled-datepicker"
          popperPlacement="bottom"
          maxDate={new Date()}
          customInput={<input style={{ textAlign: 'center', height: '25px', lineHeight: '25px', width: '150px', marginRight: '10px' }} />}
        />
        <div className="MrtJbButtonBox" style={{ display: 'flex', alignItems: 'center', marginLeft: '5px' }}>
          <button onClick={handleSelectAll} className="styled-button" style={{ marginRight: '5px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>전체 선택</button>
          <button onClick={handleDeselectAll} className="styled-button" style={{ height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>전체 해제</button>
          <CSVLink data={generateCSVData()} headers={csvHeaders} filename={`data_${selectedDateStr}.csv`} className="styled-button" style={{ marginLeft: '20px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', backgroundColor: '#1D6C41' }}>CSV 다운로드</CSVLink>
        </div>
      </div>
      <div style={{ height: height, paddingTop: '0px' }}>
        {isLoading ? (
          <div className="loading-message">데이터를 불러오는 중...</div>
        ) : !isDataAvailable ? (
          <div className="no-data-message">데이터가 없습니다.</div>
        ) : (
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
}



